import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Box,
  ButtonStyledIconLeft,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import FloatingInput from "../../../components/common/inputFields/FloatingInput";
import {
  ClientsIconWithBg,
  PlusIcon,
  SearchIcon,
} from "../../../components/styled/AllImages";
import ListTable from "../../../components/common/tables/ListTable";
import { clientsListColumns } from "../../../helpers/adminConstants/TableConstants";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import { ClientsWrapper } from "./styled";
import { LazyLoadDropdown } from "../../../components/common/inputFields/LazyLoadDropdown";
import { useForm } from "react-hook-form";
import { useDebounce } from "../../../hooks/useDebounce";
import ClientsApi from "../../../services/api/clientsApi";
import { toast } from "react-toastify";
import { getModuleOperations } from "../../../utils";

const Clients = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();

  const { register, control } = useForm();
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowsData, setRowsData] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const typesOptions = [
    { value: 1, label: t("individual") },
    { value: 2, label: t("organization") },
  ];

  const onDelete = (row) => {
    setClientId(row?.id);
    setShowDeleteModal(true);
  };

  const handleView = (client) => {
    navigate(`/clients/${client?.id}/view-client`);
  };

  const handleEdit = (client) => {
    navigate(`/clients/${client?.id}/edit-client`);
  };

  const handleSearch = (e) => {
    const query = e.target.value.trim();
    setSearchQuery(query);
  };

  const getClientsList = async (page = 1, query = "", type = null) => {
    try {
      setLoading(true);
      const { data, code } = await ClientsApi.clientsList(page, query, type);
      if (code === 200) {
        setRowsData(data?.data);
        setTotalCount(data?.total);
        setPerPage(data?.per_page);
      } else {
        setRowsData([]);
        setTotalCount(null);
        setPerPage(null);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoadingDelete(true);
      const response = await ClientsApi.deleteClient(clientId);
      if (response.code === 200) {
        toast.success(t("client_deleted_successfully"));
        setShowDeleteModal(false);
        getClientsList(activePage);
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {
    } finally {
      setLoadingDelete(false);
    }
  };

  useEffect(() => {
    getClientsList(activePage, debouncedSearchQuery, selectedType);
  }, [activePage, debouncedSearchQuery, selectedType, language]);

  return (
    <ClientsWrapper>
      <Box className="content-head">
        <Box className="d-flex align-items-center flex-wrap flex-sm-nowrap gap-3">
          <ClientsIconWithBg />
          <SubHeadingStyled size="16px" weight="700" color="var(--text)">
            {t("clients")}
          </SubHeadingStyled>
        </Box>
        <Box className="d-flex gap-2 head-item-two">
          <LazyLoadDropdown
            // label="by_type"
            display="flex"
            width="250px"
            weight="600"
            name="search_type"
            control={control}
            placeholder={t("by_type")}
            options={typesOptions}
            onChange={(val) => setSelectedType(val)}
            // value={watch("discount_type")}
            register={register}
            // errors={errors}
            validationRules={{ required: false }}
          />
          <FloatingInput
            name="search_value"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("search")}
            bg="var(--white)"
            icon={SearchIcon}
            onChange={handleSearch}
          />

          {getModuleOperations(3, 1) && (
            <ButtonStyledIconLeft
              label={t("add_client")}
              icon={PlusIcon}
              onClick={() => navigate(`/clients/create-client`)}
            />
          )}
        </Box>
      </Box>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            options={["view", "edit", "delete"]}
            rows={rowsData}
            typeId={3}
            columns={clientsListColumns}
            onDelete={onDelete}
            onOpen={handleView}
            onEdit={handleEdit}
            // onStatusToggle={handleStatusToggle}
            activePage={activePage}
            setActivePage={setActivePage}
            totalCount={totalCount}
            perPage={perPage}
          />
        )}
      </Box>
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title={t("client")}
        isLoading={loadingDelete}
        onDelete={handleDelete}
      />
    </ClientsWrapper>
  );
};

export default Clients;
